import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/auth";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/lang";
import { toast } from "sonner";
import { Check, Star } from "lucide-react";

export default function Pricing() {
    const { t } = useLang();
    const { user, plan } = useAuth();
    const nav = useNavigate();
    const [plans, setPlans] = useState([]);
    const [loadingPlan, setLoadingPlan] = useState(null);

    useEffect(() => {
        api.get("/plans").then((r) => setPlans(r.data.plans));
    }, []);

    const featuresMap = {
        basic: ["std_metrics"],
        pro: ["std_metrics", "voice_tone", "hostile", "history"],
        studio: ["std_metrics", "voice_tone", "hostile", "history", "team", "admin", "priority"],
    };

    const subscribe = async (planId) => {
        if (!user) { nav("/register"); return; }
        setLoadingPlan(planId);
        try {
            const r = await api.post("/payments/checkout", {
                plan_id: planId,
                origin_url: window.location.origin,
            });
            window.location.href = r.data.url;
        } catch (e) {
            toast.error(e.response?.data?.detail || t("common.error"));
            setLoadingPlan(null);
        }
    };

    return (
        <div className="min-h-screen bg-ink text-slate-900">
            <Navbar />
            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
                <div className="text-center max-w-2xl mx-auto mb-16">
                    <div className="mono-label mb-4">/ PRICING</div>
                    <h1 className="font-display text-5xl md:text-6xl mb-4 tracking-tighter" data-testid="pricing-title">
                        {t("pricing.title")}
                    </h1>
                    <p className="text-slate-600 text-lg">{t("pricing.subtitle")}</p>
                </div>

                <div className="grid lg:grid-cols-3 gap-px bg-slate-100 border border-slate-200" data-testid="plans-grid">
                    {plans.map((p) => {
                        const isStudio = p.id === "studio";
                        const isPro = p.id === "pro";
                        const isCurrent = plan === p.id;
                        return (
                            <div
                                key={p.id}
                                className={`bg-ink p-10 relative ${isStudio ? "lg:border-l-2 lg:border-r-2 border-brand brand-glow" : ""}`}
                                data-testid={`plan-card-${p.id}`}
                            >
                                {isPro && (
                                    <div className="absolute top-4 right-4 mono-label flex items-center gap-1 text-brand">
                                        <Star size={12} /> {t("pricing.most_popular")}
                                    </div>
                                )}
                                <div className="mono-label mb-4">/ {p.id.toUpperCase()}</div>
                                <h3 className="font-display text-3xl mb-2">{p.name}</h3>
                                <p className="text-slate-500 text-sm mb-8 min-h-[40px]">{t(`pricing.${p.id}_desc`)}</p>
                                <div className="flex items-baseline gap-2 mb-8">
                                    <span className="font-display text-6xl">${p.price.toFixed(0)}</span>
                                    <span className="text-slate-500">{t("pricing.month")}</span>
                                </div>
                                <div className="flex items-baseline gap-2 mb-8 pb-8 border-b border-slate-200">
                                    <span className="font-mono text-2xl text-brand">{p.unlimited ? "∞" : p.tokens}</span>
                                    <span className="mono-label">{p.unlimited ? t("pricing.unlimited") : t("pricing.tokens_month")}</span>
                                </div>
                                <ul className="space-y-3 mb-10 min-h-[200px]">
                                    {featuresMap[p.id]?.map((f) => (
                                        <li key={f} className="flex items-start gap-3 text-sm text-slate-700">
                                            <Check size={16} className="text-brand mt-0.5 flex-shrink-0" />
                                            <span>{t(`pricing.features.${f}`)}</span>
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    onClick={() => subscribe(p.id)}
                                    disabled={isCurrent || loadingPlan === p.id}
                                    className={`w-full justify-center ${isCurrent ? "btn-ghost cursor-default" : isStudio || isPro ? "btn-primary" : "btn-ghost"}`}
                                    data-testid={`choose-plan-${p.id}-btn`}
                                >
                                    {isCurrent ? t("pricing.current") : loadingPlan === p.id ? "..." : t("pricing.choose")}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </main>
        </div>
    );
}
